import { m, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Phone, ArrowDown, ShieldCheck, Zap, CheckCircle2 } from "lucide-react";
import { BUSINESS, HERO_IMAGE } from "@/data/site";
import { StaggerText } from "@/components/site/Reveal";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.4, 0.75]);

  return (
    <section ref={ref} id="top" className="relative min-h-[82svh] sm:min-h-[90svh] lg:min-h-[100svh] flex flex-col justify-end overflow-hidden">
      <m.div style={{ y }} className="absolute inset-0 z-0 will-change-transform">
        <img
          src={HERO_IMAGE}
          alt="The Aims Electrical team on a large commercial fit-out project in West Yorkshire"
          className="w-full h-[120%] object-cover"
          fetchPriority="high"
          decoding="async"
        />
        <m.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-black" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/20" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent" />
      </m.div>

      <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 lg:px-10 pt-32 pb-16 lg:pb-24">
        <m.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          className="font-mono text-xs tracking-[0.25em] text-brand mb-6 flex items-center gap-3"
        >
          <span className="inline-block w-8 h-px bg-brand" />
          BRADFORD · WEST YORKSHIRE
        </m.p>

        <h1 className="font-heading uppercase leading-[0.86] tracking-tight text-[13vw] sm:text-[10vw] lg:text-[7.4vw] text-brand">
          <StaggerText
            text={["Aims", <>Electrical<span className="text-brand">.</span></>]}
            ariaLabel="Aims Electrical"
            delay={0.2}
          />
        </h1>

        <div className="mt-8 lg:mt-10 max-w-2xl">
          <m.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.7 }}
            className="text-base sm:text-lg text-white/80 leading-relaxed"
          >
            Domestic & commercial electricians serving Bradford and the whole of West Yorkshire.
            Rewires, fault finding, fire alarms and round-the-clock emergency call-outs — done safely, done right.
          </m.p>

          <m.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-8 flex flex-wrap items-center gap-x-5 gap-y-4"
            data-testid="hero-trust-badges"
          >
            {[
              { k: "qualified", Icon: ShieldCheck, label: <>Fully Qualified<br className="hidden sm:block" /> &amp; Insured</> },
              { k: "emergency", Icon: Zap, label: <>24/7 Emergency<br className="hidden sm:block" /> Call-Outs</> },
              { k: "local", Icon: CheckCircle2, label: <>Reliable.<br className="hidden sm:block" /> Professional. Local.</> },
            ].map(({ k, Icon, label }, i) => (
              <div key={k} className="flex items-center gap-3">
                {i > 0 && <span className="hidden sm:block w-px h-9 bg-white/20 mr-2" aria-hidden="true" />}
                <Icon className="w-6 h-6 text-brand shrink-0" strokeWidth={2} />
                <span className="text-sm text-white/85 leading-tight">{label}</span>
              </div>
            ))}
          </m.div>

          <m.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.7 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a href={BUSINESS.phoneHref} data-testid="hero-call-cta" className="cta-pulse group inline-flex items-center gap-3 bg-red-600 text-white font-semibold px-8 py-4 rounded-full shadow-xl shadow-red-600/40 ring-1 ring-white/25 hover:bg-red-700 hover:scale-[1.04] active:scale-95 transition-all duration-200">
              <Phone className="w-5 h-5" strokeWidth={2.5} />
              Call {BUSINESS.phoneDisplay}
            </a>
            <a href="#contact" data-testid="hero-quote-cta" className="inline-flex items-center gap-3 border border-white/30 text-white font-medium px-7 py-4 hover:border-brand hover:text-brand transition-colors duration-200">
              Get a Free Quote
            </a>
          </m.div>
        </div>
      </div>

      <m.a
        href="#services"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: 0.6 }}
        className="hidden lg:flex absolute bottom-10 right-10 items-center gap-2 font-mono text-xs tracking-[0.2em] text-white/70 hover:text-white transition-colors z-10"
      >
        SCROLL <ArrowDown className="w-4 h-4 animate-bounce" />
      </m.a>
    </section>
  );
};
